import { gql } from '@apollo/client';
import {
  Button,
  Card,
  CardBody,
  SPACING_1,
  SPACING_4,
  Symbols,
  Text,
  useToasts,
} from '@mnd-frontend/ui';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useCurrentPressroom } from '../../../contexts/CurrentPressroomContext';
import { useNewsroomSetup } from '../../../contexts/NewsroomSetupContext';
import { useGoLiveMutation, useSeenOnboardingWizardMutation } from '../../../generated/hooks';
import { track } from '../../../lib/tracker';

gql`
  mutation seenOnboardingWizard($pressroomId: ID!) {
    noticeConfirm(input: { notice: onboardingWizard, pressroomId: $pressroomId }) {
      pendingNotices {
        id
        onboardingWizard
      }
      pressroom {
        id
        dashboard {
          id
        }
      }
    }
  }

  mutation goLive($id: ID!) {
    pressroomGoLive(input: { id: $id }) {
      pressroom {
        id
        livePublicationCount
        ...NotLivePressroom
      }
      user {
        id
        onboarding(pressroomId: $id) {
          id
          completed
        }
      }
      error {
        message
      }
    }
  }
`;

const Body = styled(CardBody)`
  align-items: center;
  display: flex;
  flex-direction: column;
  max-width: 40rem;
  padding: ${SPACING_4};
  margin: auto;

  i {
    height: 12rem;
    width: 12rem;
  }
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SPACING_1};
  align-items: center;
`;

const GoLive = () => {
  const { pressroomId } = useCurrentPressroom();
  const { showSuccessToast, showFailureToast } = useToasts();
  const [t] = useTranslation();
  const cardRef = useRef<HTMLDivElement>(null);
  const { setNewsroomSetupStep } = useNewsroomSetup();

  const [goLive, { loading }] = useGoLiveMutation({
    onError: () => {
      showFailureToast(t('common_general_error'));
    },
    onCompleted: data => {
      const error = data?.pressroomGoLive?.error;
      if (error) {
        showFailureToast(error?.message ?? t('common_general_error'));
        return;
      }

      if (data.pressroomGoLive?.pressroom?.newsroomUrl) {
        showSuccessToast(t('not_live_alert_live_toaster'));
      } else {
        showSuccessToast(t('not_live_alert_live_no_newsroom_toaster'));
      }
      track({
        event: 'Newsroom Setup: Go Live - Proceed',
        traits: {
          step: 5,
          numberOfLivePublications: data.pressroomGoLive?.pressroom?.livePublicationCount ?? 0,
        },
      });
      setNewsroomSetupStep(undefined);
    },
  });

  const [seenOnboardingWizard] = useSeenOnboardingWizardMutation({
    onCompleted: () => setNewsroomSetupStep('company-info'),
    variables: {
      pressroomId,
    },
  });

  useEffect(() => {
    if (cardRef.current) {
      cardRef.current?.scrollIntoView({
        behavior: 'smooth',
      });
    }
    track({
      event: 'Newsroom Setup: Go Live',
      traits: { step: 5 },
    });
  }, []);

  return (
    <div ref={cardRef}>
      <Card>
        <Body>
          <Symbols.RocketLaunch size="large" />
          <Text as="h1" variant="headlineLarge" marginBottom="SPACING_2" marginTop="SPACING_3">
            {t('onboarding-go-live_title')}
          </Text>
          <Text as="p" variant="body" color="GREY_800" textAlign="center" marginBottom="SPACING_5">
            {t('onboarding-go-live_body')}
          </Text>
          <Buttons>
            <Button
              type="button"
              loading={loading}
              onClick={() => {
                goLive({
                  variables: {
                    id: pressroomId,
                  },
                });
              }}
            >
              {t('not_live_alert_trusted_go_live')}
            </Button>
            <Button
              type="button"
              configuration="text"
              onClick={() => {
                seenOnboardingWizard();
                setNewsroomSetupStep(undefined);
                track({
                  event: 'Newsroom Setup: Clicked Not ready yet',
                  traits: {
                    step: 5,
                  },
                });
              }}
            >
              {t('onboarding-go-live_secondary-button')}
            </Button>
          </Buttons>
        </Body>
      </Card>
    </div>
  );
};

export default GoLive;
